<template>
  <div>
    <b-card-actions
      :title="$t('Dados da Contagem')"
      no-actions
      :show-loading="busy || fetching"
    >
      <b-row>
        <b-col md="2">
          <FormulateInput
            id="status"
            v-model="stockCountingForm.status"
            type="label"
            is-badge
            :label="$t('Status')"
            :options="stockCountingStatusOptions()"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="store"
            :value="storeName"
            type="label"
            :label="$t('Loja')"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="description"
            v-model="stockCountingForm.description"
            type="label"
            :label="$t('Descrição')"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="percentCompleted"
            v-model="stockCountingForm.percentCompleted"
            :label="$t('Perc. Completado')"
            type="label"
            filter="percentage"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="skusCount"
            :value="stockCountingForm.skusCount"
            :label="$t('Qtd. SKUs')"
            type="label"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col md="3">
          <FormulateInput
            id="group"
            :value="groupName"
            type="label"
            :label="$t('Grupo')"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="subgroup"
            :value="subgroupName"
            type="label"
            :label="$t('Subgrupo')"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="brand"
            :value="brandName"
            type="label"
            :label="$t('Marca')"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="department"
            :value="departmentName"
            type="label"
            :label="$t('Departamento')"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="created-at"
            :value="`${$options.filters.date(stockCountingForm.createdDate)} / ${
              stockCountingForm.createdBy || '-'
            }`"
            :label="$t('Criado em / por')"
            type="label"
          />
        </b-col>
        <b-col
          v-if="stockCountingForm.deviationThresholdPercent != null"
          md="2"
        >
          <FormulateInput
            id="deviationThreshold"
            :value="`${stockCountingForm.deviationThresholdPercent}%`"
            :label="$t('Limite de desvio')"
            type="label"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <b-card-actions
      v-if="stockCountingForm.completedDate"
      :title="$t('Dados da Finalização')"
      no-actions
      :show-loading="busy || fetching"
    >
      <b-row>
        <b-col md="4">
          <FormulateInput
            id="completed_date"
            :value="`${$options.filters.datetime(stockCountingForm.completedDate)} / ${
              stockCountingForm.completedUser ? stockCountingForm.completedUser.name : '-'
            }`"
            :label="$t('Finalizado em / por')"
            type="label"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <b-card-actions
      v-if="stockCountingForm.cancelationDate"
      :title="$t('Dados do Cancelamento')"
      no-actions
      :show-loading="busy || fetching"
    >
      <b-row>
        <b-col md="3">
          <FormulateInput
            id="cancelation_date"
            :value="`${$options.filters.datetime(stockCountingForm.cancelationDate)} / ${
              stockCountingForm.cancelationUser ? stockCountingForm.cancelationUser.name : '-'
            }`"
            :label="$t('Cancelado em / por')"
            type="label"
          />
        </b-col>
        <b-col>
          <FormulateInput
            id="cancelation_reason"
            :value="stockCountingForm.cancelationReason"
            :label="$t('Motivo')"
            type="label"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="getSkus"
    >
      <div
        slot="title"
        class="d-flex align-items-center flex-wrap"
      >
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
        <e-button
          id="download"
          class="ml-1 btn-sm-block"
          variant="primary"
          icon="download"
          :busy="fetching"
          :text="$t('Baixar Relatório')"
          @click="downloadReport"
        />
      </div>

      <b-table
        ref="table-skus"
        show-empty
        striped
        responsive
        class="mb-1 bordered"
        :busy="busy"
        :fields="fields"
        :items="stockCountingSkus"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getSkus"
      >
        <template #cell(name)="{ item }">
          <e-product-router-link
            :sku-id="item.skuId"
            :product-id="item.productId"
            :product-name="item.name"
            :ean="item.ean"
          />
        </template>

        <template #cell(stockCounted)="row">
          <div :class="!readOnly ? 'stock-counted-cell' : ''">
            <e-button
              v-if="!readOnly && isInProgress && row.item.completed"
              icon="trash"
              variant="danger"
              class="p-5"
              :title="$t('Excluir contagem')"
              @click="resetStockCounted(row.item)"
            />
            <FormulateInput
              v-if="!readOnly"
              :id="`sku-stock-counted-${row.index}`"
              v-model="row.item.stockCounted"
              type="text-number"
              :precision="3"
              class="text-right mb-0 stock-counted-input"
              @blur-context="updateStockCounted(row.item)"
            />
            <span v-else-if="row.item.stockCounted != null">
              {{ row.item.stockCounted | number(3, true) }}
            </span>
            <span v-else>-</span>
          </div>
        </template>

        <template #cell(stockInSystem)="{ item }">
          <span v-if="!canSeeStock">-</span>
          <span v-else>{{ item.stockInSystem | number(3, true) }}</span>
        </template>

        <template #cell(stockDifference)="{ item }">
          <span v-if="!canSeeStock">-</span>
          <span
            v-else
            :class="addValueOperationStyle(item.stockDifference)"
          >
            {{ item.stockDifference | number(3, true) }}
          </span>
        </template>

        <template #cell(hasDivergence)="{ item }">
          <span v-if="!canSeeStock && !item.hasDivergence">-</span>
          <span
            v-else-if="item.hasDivergence"
            class="text-danger font-weight-bold"
          >
            {{ $t('Sim') }}
          </span>
          <span
            v-else-if="item.hasDivergence === false"
            class="text-success"
          >
            {{ $t('Não') }}
          </span>
          <span v-else>-</span>
        </template>
      </b-table>

      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="table-skus"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <stock-counting-cancel-modal
      ref="cancelModal"
      @after-confirm="onReturn"
    />

    <fab
      :main-tooltip="$t('Ações')"
      main-icon="keyboard_command_key"
      :actions="actions"
      :busy="busy"
      @save="onFinishCounting"
      @return="onReturn"
      @cancel="onCancel"
    />
  </div>
</template>

<script>
import { BCol, BRow, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import { mapActions, mapState } from 'vuex'
import { downloader, stockDomain } from '@/mixins'
import {
  EPaginationSummary,
  EPageSizeSelector,
  EButton,
  EProductRouterLink,
} from '@/views/components'
import fab from '@/views/components/FAB.vue'
import StockCountingCancelModal from './components/StockCountingCancelModal.vue'

const STOCK_SYSTEM_COLUMNS = ['stockInSystem', 'stockDifference', 'stockDeviationPercent', 'hasDivergence']

export default {
  name: 'StockCountingMaintain',
  components: {
    BCardActions,
    BCol,
    BRow,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    EButton,
    fab,
    EProductRouterLink,
    StockCountingCancelModal,
  },

  mixins: [stockDomain, downloader],

  props: {
    readOnlyPage: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      busy: false,
      fetching: false,
      readOnly: false,
    }
  },

  computed: {
    ...mapState('pages/stock/stockCounting/stockCountingMaintain', [
      'stockCountingForm',
      'stockCountingSkus',
      'sorting',
      'paging',
      'filters',
      'busy',
    ]),

    isInProgress() {
      return this.stockCountingForm.status === this.stockCountingStatusEnum.IN_PROGRESS
    },

    canSeeStock() {
      return this.$can('SeeStock', 'StockPosition')
    },

    storeName() {
      return this.$options.filters.storeName(this.stockCountingForm.store) || '-'
    },
    groupName() {
      return this.stockCountingForm.group?.name || '-'
    },
    subgroupName() {
      return this.stockCountingForm.subgroup?.name || '-'
    },
    brandName() {
      return this.stockCountingForm.brand?.name || '-'
    },
    departmentName() {
      return this.stockCountingForm.department?.name || '-'
    },

    fields() {
      const baseFields = [
        {
          label: this.$t('Produto'),
          key: 'name',
          sortable: true,
          thStyle: { minWidth: '300px' },
        },
        {
          label: this.$t('EAN'),
          key: 'ean',
          class: 'text-center',
          thStyle: { width: '130px' },
          formatter: value => value || '-',
        },
        {
          label: this.$t('UN.'),
          key: 'unitOfMeasurement',
          class: 'text-center',
          thStyle: { width: '80px' },
          formatter: value => value || '-',
        },
        {
          label: this.$t('Estoque Contado'),
          key: 'stockCounted',
          class: 'text-center',
          thStyle: { width: '160px' },
        },
        {
          label: this.$t('Estoque no Sistema'),
          key: 'stockInSystem',
          class: 'text-center',
          thStyle: { width: '130px' },
        },
        {
          label: this.$t('Diferença'),
          key: 'stockDifference',
          class: 'text-center',
          thStyle: { width: '110px' },
        },
        {
          label: this.$t('Desvio %'),
          key: 'stockDeviationPercent',
          class: 'text-center',
          thStyle: { width: '100px' },
          formatter: value =>
            this.canSeeStock ? `${this.$options.filters.number(value)} %` : '-',
        },
        {
          label: this.$t('Divergência'),
          key: 'hasDivergence',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
      ]

      if (!this.canSeeStock) {
        const fields = baseFields.filter(f => !STOCK_SYSTEM_COLUMNS.includes(f.key))
        if (this.stockCountingForm.status === this.stockCountingStatusEnum.COMPLETED) {
          fields.push({
            label: this.$t('Divergência'),
            key: 'hasDivergence',
            class: 'text-center',
            thStyle: { width: '100px' },
          })
        }
        return fields
      }

      

      return baseFields
    },

    actions() {
      return [
        {
          name: 'return',
          icon: 'arrow_back',
          color: 'red',
          tooltip: this.$t('Voltar'),
        },
        {
          name: 'save',
          icon: 'save',
          color: 'green',
          tooltip: this.$t('Finalizar Contagem'),
          hide:
            this.readOnly ||
            !this.isInProgress ||
            (this.stockCountingForm.percentCompleted != null &&
              this.stockCountingForm.percentCompleted < 1.0),
        },
        {
          name: 'cancel',
          icon: 'close',
          color: 'red',
          tooltip: this.$t('Cancelar contagem'),
          hide:
            !this.$can('Cancel', 'StockCounting') ||
            !this.isInProgress,
        },
      ].filter(a => !a.hide)
    },
  },

  async mounted() {
    this.fetching = true
    this.readOnly = this.readOnlyPage

    try {
      await Promise.all([
        this.fetchCounting(this.$route.params.id),
        this.fetchCountingSkus(this.$route.params.id),
      ])

      if (!this.isInProgress) {
        this.readOnly = true
      }
    } catch (error) {
      this.showError({ error, message: this.$t('Não foi possível obter os dados da Contagem') })
      this.$router.push({ name: 'stock-counting-list' })
    } finally {
      this.fetching = false
    }
  },

  methods: {
    ...mapActions('pages/stock/stockCounting/stockCountingMaintain', [
      'fetchCounting',
      'fetchCountingSkus',
      'saveSkuStockCounted',
      'resetSkuStockCounted',
      'finishCounting',
      'generateReport',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
      'cleanState',
    ]),

    async getSkus() {
      this.$nextTick(async () => {
        this.fetching = true
        try {
          await this.fetchCountingSkus(this.$route.params.id)
        } catch (error) {
          this.showError({
            error,
            message: this.$t('Não foi possível obter os produtos da Contagem'),
          })
        } finally {
          this.fetching = false
        }
      })
    },

    async updateStockCounted(sku) {
      if (sku.stockCounted != null) {
        this.busy = true
        try {
          const result = await this.saveSkuStockCounted({
            skuId: sku.skuId,
            stockCounted: sku.stockCounted,
          })
          // eslint-disable-next-line no-param-reassign
          sku.completed = result.completed
          // eslint-disable-next-line no-param-reassign
          sku.stockCounted = result.stockCounted
          // eslint-disable-next-line no-param-reassign
          sku.stockDeviationPercent = result.stockDeviationPercent
          // eslint-disable-next-line no-param-reassign
          sku.stockDifference = result.stockDifference
          // eslint-disable-next-line no-param-reassign
          sku.hasDivergence = result.hasDivergence
        } catch (error) {
          this.showError({ error, message: this.$t('Não foi possível atualizar o dado') })
          this.getSkus()
        } finally {
          this.busy = false
        }
      }
    },

    async resetStockCounted(sku) {
      if (await this.confirm({ text: this.$t('Tem certeza que deseja excluir esta contagem?') })) {
        this.busy = true
        this.fetching = true
        try {
          await this.resetSkuStockCounted(sku.skuId)
          // eslint-disable-next-line no-param-reassign
          sku.completed = false
          // eslint-disable-next-line no-param-reassign
          sku.stockCounted = null
        } catch (error) {
          this.showError({ error, message: this.$t('Não foi possível atualizar o dado') })
        } finally {
          this.busy = false
          this.fetching = false
        }
      }
    },

    async downloadReport() {
      try {
        this.fetching = true
        const reportData = await this.generateReport(this.$route.params.id)

        if (reportData) {
          this.forceFileDownloadXlsx(
            reportData,
            `Contagem_${this.$route.params.id}_-Loja_${this.stockCountingForm?.store?.id}`
          )
        } else {
          this.showError({ message: this.$t('Não foi possível recuperar os dados do relatório') })
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.getSkus()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getSkus()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getSkus()
    },

    addValueOperationStyle(value) {
      if (value == null) return ''
      if (value > 0) return 'text-success'
      if (value < 0) return 'text-danger'
      return ''
    },

    async onFinishCounting() {
      if (
        await this.confirm({
          text: this.$t('Confirma a finalização da contagem? Esta ação não pode ser desfeita.'),
        })
      ) {
        this.busy = true
        try {
          await this.finishCounting()
          this.showSuccess({ message: this.$t('Contagem finalizada com sucesso!') })
          await Promise.all([
            this.fetchCounting(this.$route.params.id),
            this.fetchCountingSkus(this.$route.params.id),
          ])
          this.readOnly = true
        } catch (error) {
          this.showError({ error, message: this.$t('Não foi possível finalizar a contagem') })
        } finally {
          this.busy = false
        }
      }
    },

    onReturn() {
      this.$router.push({ name: 'stock-counting-list' })
    },

    onCancel() {
      this.$refs.cancelModal.show(this.stockCountingForm.id)
    },
  },
}
</script>

<style lang="scss" scoped>
.stock-counted-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.stock-counted-input {
  flex: 1;
  min-width: 90px;
}
</style>
